/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, ReactNode} from 'react';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {AppliedFilterBarFavoriteState} from '../../filters/AppliedFilterBarTypes';
import {FilterDrawerFavoriteFilledIcon} from '../../icons/current/FilterDrawerFavoriteFilledIcon';
import {FilterDrawerFavoriteIcon} from '../../icons/current/FilterDrawerFavoriteIcon';
import {FilterFavoriteWorkingIcon} from '../../icons/current/FilterFavoriteWorkingIcon';
import {FilterIcon} from '../../icons/current/FilterIcon';
import {TrackingFilterIcon} from '../../icons/current/TrackingFilterIcon';
import Render from '../../render/Render';
import {dlsColor} from '../../theme/theme';
import {IconSize} from '../../types/types';
import {FilterDrawerFavoriteState} from './types';

interface FilterDrawerFooterProps {
  applyDisabled?: boolean;
  clearAllDisabled?: boolean;
  isCurrentTheme?: boolean;
  isLegacyTheme?: boolean;
  classes: Record<string, string>;
  isFavorite: boolean;
  // the current theme's heart - see `getFavoriteIcon` ...
  favoriteState?: FilterDrawerFavoriteState;
  message?: ReactNode;
  onApply?: () => void;
  onClearAll?: () => void;
  onToggleFavorite: () => void;
}

/**
 * Displays filter drawer actions for clear, apply, and favorite state, with an
 * optional message - a limit warning, for one - stacked above them.
 *
 * ```typescript
 * import FilterDrawerFooter from '@ion/dls/src/components/drawer/FilterDrawerFooter';
 *
 * ...
 *
 * <FilterDrawerFooter applyDisabled={exceedsLimit}
 *   clearAllDisabled={!selectedCount}
 *   classes={classes}
 *   isFavorite={favorite}
 *   message={limitWarning}
 *   onApply={applyFilters}
 *   onClearAll={clearFilters}
 *   onToggleFavorite={toggleFavorite} />
 * ```
 */
// the current theme's heart: a heart with a pencil while the selected
// favorite has unsaved changes, filled red while one is selected, otherwise
// the outline the legacy theme always shows ...
const getFavoriteIcon = (isCurrentTheme: boolean, favoriteState?: FilterDrawerFavoriteState) => {
  let icon = <FilterDrawerFavoriteIcon color='currentColor' />;
  if (isCurrentTheme && favoriteState === AppliedFilterBarFavoriteState.Working) {
    icon = <FilterFavoriteWorkingIcon />;
  } else if (isCurrentTheme && favoriteState === AppliedFilterBarFavoriteState.Saved) {
    icon = <FilterDrawerFavoriteFilledIcon />;
  }
  return icon;
};

export const FilterDrawerFooter = memo(({
  applyDisabled = false,
  clearAllDisabled,
  classes,
  isFavorite,
  favoriteState,
  message,
  onApply,
  onClearAll,
  onToggleFavorite,
  isLegacyTheme = false,
  isCurrentTheme = false
}: FilterDrawerFooterProps) => (
  <div className={classnames(classes.footer, {[classes.footerWithMessage]: !!message})}>
    {message}
    <div className={classes.footerInner}>
      <button type='button'
        className={classes.clearButton}
        disabled={clearAllDisabled}
        onClick={onClearAll}>
        {translate('filters:drawer.clearAll')}
      </button>
      <button type='button'
        className={classes.applyButton}
        disabled={applyDisabled}
        onClick={onApply}>
        <Render.If condition={isLegacyTheme}>
          <FilterIcon color={dlsColor('white')} size={IconSize.medium} />
        </Render.If>
        <Render.If condition={isCurrentTheme}>
          <TrackingFilterIcon color={dlsColor('white')} />
        </Render.If>
        <span>{translate('filters:drawer.apply')}</span>
      </button>
      <button type='button'
        className={classnames(classes.favoriteButton, {
          [classes.favoriteButtonSelected] : isFavorite
        })}
        aria-pressed={isFavorite}
        onClick={onToggleFavorite}
        aria-label={translate('filters:drawer.favorite')}>
        {getFavoriteIcon(isCurrentTheme, favoriteState)}
      </button>
    </div>
  </div>
));

export default FilterDrawerFooter;
